import React, { useState, useEffect, useRef } from 'react';
import {
  Eye,
  EyeOff,
  User,
  Lock,
  ArrowRight,
  UserPlus,
  Building2,
  BadgeCheck,
  Wifi,
  WifiOff
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translateWithVars } from '../utils/i18n/translateWithVars';
import LanguageSwitcher from '../components/LanguageSwitcher';
import AuthBrandLogo from '../components/AuthBrandLogo';
import { authService } from '../services/supabaseService';
import { useLoadingState } from '../hooks/useSharedState';
import businessInfoService from '../services/businessInfoService';
import '../styles/auth-professional.css';
import {
  readStoredSessionBusiness,
  clearCorruptedSessionUser,
  establishAdminTransitionSession,
  persistBusinessSessionPair,
  persistManagerCode
} from '../services/offlineAuthSession';
import { setAppCurrency } from '../services/appPreferencesStorage';
import { clearBusinessLogoutIntent } from '../utils/businessLogoutFlag';
import { sessionSet, sessionRemove } from '../infrastructure/storage/sessionAccess';
import {
  loginWorkerByNameAndCode,
  PENDING_WORKER_CASH_LOGIN_KEY
} from '../services/workerLoginService';
import { normalizeWorkerPermissions } from '../utils/workerPermissions';
import sessionRecorder from '../utils/sessionRecorder';
import lockoutService from '../services/lockoutService';
import { warmShellRouteChunks } from '../utils/navRoutePrefetch';
import {
  buildOfflineLoginDebug,
  formatLoginDebugLines,
  logOfflineLoginDebug
} from '../utils/authLoginDebug';
import { kvGet, kvSet, kvRemove } from '../infrastructure/storage/kvAccess';

function warmShellRouteChunk() {
  warmShellRouteChunks();
}

/** Kick the shell chunk as soon as Login paints — not after auth dispatch. */
function warmManagerRoutesAfterLogin() {
  warmShellRouteChunk();
}

const REMEMBER_USERNAME_KEY = 'aman_auth_remember_username';
const APP_VERSION = process.env.REACT_APP_VERSION || '0.2.0';

const Login = ({ onShowRegister, onShowForgotPassword, registrationAllowed = true }) => {
  const { t, isRTL } = useLanguage();
  const ta = (key, vars) => translateWithVars(t(`authPage.${key}`), vars);
  const [loginMode, setLoginMode] = useState('business');
  const [form, setForm] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const usernameRef = useRef(null);
  const { isLoading, setIsLoading } = useLoadingState();
  const [error, setError] = useState('');
  const [loginDebug, setLoginDebug] = useState(null);
  const [showLoginDebug, setShowLoginDebug] = useState(false);
  // FIX: Track AppData rehydration so login waits for it on reinstall
  const rehydratePromiseRef = useRef(null);
  const rehydratedRef = useRef(false);

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    clearBusinessLogoutIntent();
  }, []);

  // Warm the post-login shell chunk while the user is still on Login —
  // waiting until after authStateChanged left a long Suspense skeleton.
  useEffect(() => {
    let idleId = null;
    let timerId = null;
    const run = () => warmShellRouteChunk();
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(run, { timeout: 1500 });
    } else {
      timerId = window.setTimeout(run, 400);
    }
    return () => {
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timerId != null) window.clearTimeout(timerId);
    };
  }, []);

  // FIX: Eagerly rehydrate auth storage from AppData on mount.
  // This runs before the user clicks Login — so reinstalled users have their
  // account available in KV/localStorage by the time they submit the form.
  useEffect(() => {
    if (rehydratedRef.current) return;
    const promise = import('../utils/authCredentialRead')
      .then(({ ensureAuthStorageRehydrated }) => ensureAuthStorageRehydrated())
      .catch(() => {})
      .finally(() => {
        rehydratedRef.current = true;
      });
    rehydratePromiseRef.current = promise;
  }, []);

  useEffect(() => {
    try {
      const saved = kvGet(REMEMBER_USERNAME_KEY);
      if (typeof saved === 'string' && saved.trim()) {
        setForm((prev) => ({ ...prev, username: saved.trim() }));
        setRememberMe(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    usernameRef.current?.focus();
  }, [loginMode]);

  const persistRememberPreference = () => {
    try {
      if (rememberMe && form.username.trim()) {
        kvSet(REMEMBER_USERNAME_KEY, form.username.trim());
      } else {
        kvRemove(REMEMBER_USERNAME_KEY);
      }
    } catch {
      /* ignore */
    }
  };
  const handleChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
    setLoginDebug(null);
    setShowLoginDebug(false);
  };

  const switchLoginMode = (mode) => {
    setLoginMode(mode);
    setError('');
    setLoginDebug(null);
    setShowLoginDebug(false);
    setShowPassword(false);
    setForm((prev) => ({
      username: rememberMe && mode === 'business' ? prev.username : '',
      password: ''
    }));
  };

  const applyBusinessLoginFailure = (offlineAuthService, loginResult) => {
    const debug = loginResult?.debug || buildOfflineLoginDebug(form.username, loginResult);
    logOfflineLoginDebug(debug);
    setLoginDebug(debug);
    setShowLoginDebug(false);

    const code = loginResult?.errorCode;
    // Prefer explicit offline codes — never bury WRONG_PASSWORD behind heuristics
    if (code === 'WRONG_PASSWORD') {
      setError(ta('errWrongPasswordLocal'));
      return;
    }
    if (code === 'PASSWORD_MISSING') {
      setError(ta('errPasswordLostOffline'));
      return;
    }
    if (code === 'ACCOUNT_NOT_FOUND') {
      const offlineAccountCount = offlineAuthService.getBusinesses().length;
      if (offlineAccountCount === 0) {
        setError(registrationAllowed ? ta('errNoLocalUseRegister') : ta('errNoLocalLoginOnly'));
        return;
      }
      if (debug?.localBusinessCount > 0 && debug?.username) {
        setError(translateWithVars(ta('errUseRegisteredUsername'), { username: debug.username }));
        return;
      }
      setError(ta('errInvalidCredentials'));
      return;
    }

    const offlineAccountCount = offlineAuthService.getBusinesses().length;
    const offlineAccountMissing = offlineAccountCount === 0;
    const normalizedLogin = String(form.username || '').trim().toLowerCase();
    const hasLocalAccount = offlineAuthService.getBusinesses().some((b) => {
      const u = String(b.username || '').trim().toLowerCase();
      const e = String(b.email || '').trim().toLowerCase();
      return u === normalizedLogin || e === normalizedLogin;
    });

    if (offlineAccountMissing) {
      setError(registrationAllowed ? ta('errNoLocalUseRegister') : ta('errNoLocalLoginOnly'));
    } else if (hasLocalAccount || debug?.accountFound) {
      setError(
        loginResult?.diagnostic?.hasStoredPassword === false
          ? ta('errPasswordLostOffline')
          : ta('errWrongPasswordLocal')
      );
    } else if (debug?.localBusinessCount > 0 && debug?.username) {
      setError(translateWithVars(ta('errUseRegisteredUsername'), { username: debug.username }));
    } else {
      setError(ta('errInvalidCredentials'));
    }
  };

  const handleWorkerSubmit = async () => {
    const workerResult = await loginWorkerByNameAndCode(form.username, form.password);
    if (!workerResult.success) {
      const errorMap = {
        NAME_AND_CODE_REQUIRED: ta('errWorkerNameCodeRequired'),
        INVALID_WORKER_CREDENTIALS: ta('errInvalidWorkerCredentials'),
        WORKER_DEACTIVATED: ta('errWorkerDeactivated'),
        CASH_REGISTER_PAUSED: ta('errCashRegisterPaused'),
        NO_LOCAL_BUSINESS: ta('errNoLocalBusinessForWorker')
      };
      setError(errorMap[workerResult.errorCode] || ta('errInvalidWorkerCredentials'));
      return;
    }

    const { business, worker } = workerResult;

    const pauseStatus = sessionRecorder.isCashRegisterPaused(business.id);
    const resumedFromPause =
      pauseStatus && String(pauseStatus.workerId) === String(worker.id);

    sessionSet(PENDING_WORKER_CASH_LOGIN_KEY, {
      workerId: worker.id,
      workerName: worker.name,
      workerCode: worker.code,
      permissions: normalizeWorkerPermissions(worker.permissions),
      resumedFromPause
    });

    // Cashier only — machine lock for Supermarché/Hybrid
    const { linkLocalBusinessToThisInstallation } = await import('../services/authLinkingService');
    const link = await linkLocalBusinessToThisInstallation(business, {
      awaitBootstrap: false,
      dispatchAuthEvent: true,
      assignDefaultRole: false,
      clearRole: false,
      identity: 'cashier'
    });
    if (!link.success) {
      sessionRemove(PENDING_WORKER_CASH_LOGIN_KEY);
      setError(link.message || ta('errMachineBinding'));
      return;
    }

    // Cashier on POS: full automatic cycle (hybrid lots + mirror history if plan allows)
    try {
      const { scheduleAutomaticCloudSync } = await import('../services/parallelCloudFieldSync');
      scheduleAutomaticCloudSync(business.id, { reason: 'login' });
    } catch {
      /* cloud optional */
    }

    lockoutService.resetLockoutOnSuccess();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    persistRememberPreference();
    // Start shell chunk before auth dispatch so Suspense rarely blocks first paint.
    warmShellRouteChunk();

    // FIX: Wait for AppData rehydration if it hasn't finished yet.
    // On reinstall (empty localStorage/IDB) this ensures credentials are available.
    if (!rehydratedRef.current && rehydratePromiseRef.current) {
      try {
        await rehydratePromiseRef.current;
      } catch {
        /* ignore — proceed anyway */
      }
    }

    try {
      if (loginMode === 'worker') {
        await handleWorkerSubmit();
        return;
      }

      let result;

      // Optional admin login (nur wenn REACT_APP_ENABLE_TEMP_ADMIN_LOGIN=true + Env-Credentials)
      result = await authService.adminSignIn(form.username, form.password);
      if (result.success && result.type === 'admin') {
        // Persist admin session first so App switches to AdminDashboard immediately.
        establishAdminTransitionSession();

        try {
          const cloudAdmin = await authService.signInSupabaseAdminForDashboard();
          if (!cloudAdmin.success && !cloudAdmin.skipped) {
            console.warn(
              '[Admin] Cloud admin JWT missing — list stays empty until REACT_APP_SUPABASE_ADMIN_* is valid:',
              cloudAdmin.error
            );
          }
        } catch (adminCloudErr) {
          console.warn(
            '[Admin] Supabase admin bridge skipped:',
            adminCloudErr?.message || adminCloudErr
          );
        }
        establishAdminTransitionSession();

        lockoutService.resetLockoutOnSuccess();
        setIsLoading(false);
        return;
      }
      
      // Offline Login (localStorage + IndexedDB)
      const { offlineAuthService } = await import('../services/offlineAuthService');
      const offlineLoginResult = await offlineAuthService.login(form.username, form.password);
      result = offlineLoginResult;
      
      if (result.success) {
        const { linkLocalBusinessToThisInstallation } = await import('../services/authLinkingService');
        const link = await linkLocalBusinessToThisInstallation(result.business, {
          awaitBootstrap: false,
          dispatchAuthEvent: true,
          assignDefaultRole: false,
          clearRole: true,
          identity: 'manager'
        });
        if (!link.success) {
          setError(link.message || ta('errMachineBinding'));
          setIsLoading(false);
          return;
        }

        // Local-only account: push Auth + businesses row when internet is up
        // so admin panel can approve (no manual Settings → Upgrade Cloud required).
        try {
          const {
            getLocalCloudLinkStatus,
            ensureLocalBusinessRegisteredInCloud,
            startLocalCloudRegisterAutoFlush
          } = await import('../services/upgradeLocalBusinessToCloud');
          startLocalCloudRegisterAutoFlush();
          const linkStatus = getLocalCloudLinkStatus(result.business?.id);
          if (!linkStatus.linked) {
            const up = await ensureLocalBusinessRegisteredInCloud({
              businessId: result.business.id,
              password: form.password
            });
            if (!up.success) {
              console.warn(
                '[Login] local→cloud register deferred:',
                up.errorCode || up.error
              );
            }
          }
        } catch (autoCloudErr) {
          console.warn(
            '[Login] auto local→cloud register skipped:',
            autoCloudErr?.message || autoCloudErr
          );
        }

        if (result.business?.email) {
          try {
            const { ensureSupabaseBusinessSession } = await import('../services/supabaseAuthBridge');
            const session = await ensureSupabaseBusinessSession(result.business.email, form.password);
            if (session.success) {
              // Refresh cloud_plan / subscription from Supabase so Mirror gate stays current
              try {
                const { refreshCloudPlanFromServer } = await import('../services/cloudPlanRefresh');
                await refreshCloudPlanFromServer(result.business.id);
              } catch (planRefreshErr) {
                console.warn('cloud_plan refresh skipped:', planRefreshErr?.message || planRefreshErr);
              }
              try {
                const { authService } = await import('../services/supabaseService');
                const cloud = await authService.getCurrentUser();
                if (cloud.success && cloud.type === 'business' && cloud.profile && result.business?.id) {
                  offlineAuthService.updateBusiness(result.business.id, {
                    cloudId: cloud.profile.id,
                    cloud_plan: cloud.profile.cloud_plan || 'none',
                    subscription_type: cloud.profile.subscription_type || null,
                    subscription_amount: cloud.profile.subscription_amount ?? null,
                    subscription_expiry_date: cloud.profile.subscription_expiry_date || null,
                    supabaseAuth: true
                  });
                  const { persistBusinessSessionPair, readStoredSessionUser, readStoredCurrentBusiness } =
                    await import('../services/offlineAuthSession');
                  const curUser = readStoredSessionUser();
                  const curBiz = readStoredCurrentBusiness();
                  if (curUser?.business && curBiz) {
                    persistBusinessSessionPair(
                      {
                        ...curUser,
                        business: {
                          ...curUser.business,
                          cloudId: cloud.profile.id,
                          cloud_plan: cloud.profile.cloud_plan || 'none',
                          subscription_type: cloud.profile.subscription_type || null,
                          subscription_amount: cloud.profile.subscription_amount ?? null,
                          subscription_expiry_date: cloud.profile.subscription_expiry_date || null
                        }
                      },
                      {
                        ...curBiz,
                        cloudId: cloud.profile.id,
                        cloud_plan: cloud.profile.cloud_plan || 'none'
                      },
                      { dispatchAuthEvent: false }
                    );
                  }
                }
              } catch (planRefreshErr) {
                console.warn('cloud_plan refresh skipped:', planRefreshErr?.message || planRefreshErr);
              }
              const { drainPendingSyncOnce } = await import('../domain/sync/electronSyncOrchestrator');
              void drainPendingSyncOnce(40).catch(() => {});
              try {
                const { scheduleAutomaticCloudSync } = await import(
                  '../services/parallelCloudFieldSync'
                );
                scheduleAutomaticCloudSync(result.business?.id, { reason: 'login' });
              } catch {
                /* full cloud optional */
              }
            }
          } catch (authBridgeErr) {
            console.warn('Supabase session bridge after local login:', authBridgeErr?.message || authBridgeErr);
          }
        }

        lockoutService.resetLockoutOnSuccess();
        warmManagerRoutesAfterLogin();
        setIsLoading(false);
        return;
      }

      // Wrong/missing local password: still allow Supabase Auth attempt
      const localPasswordFail = ['WRONG_PASSWORD', 'PASSWORD_MISSING'].includes(
        offlineLoginResult?.errorCode
      );

      const offlineAccountCount = offlineAuthService.getBusinesses().length;
      const offlineAccountMissing = offlineAccountCount === 0;
      const normalizedLogin = String(form.username || '').trim().toLowerCase();
      const hasLocalAccount = offlineAuthService.getBusinesses().some((b) => {
        const u = String(b.username || '').trim().toLowerCase();
        const e = String(b.email || '').trim().toLowerCase();
        return u === normalizedLogin || e === normalizedLogin;
      });

      const tryCloud = Boolean(
        process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_ANON_KEY
      );

      if (tryCloud) {
        result = await authService.businessSignIn(form.username, form.password);

        if (!result.success && result.offline) {
          // Real network failure — keep local wrong-password message if that was the case
          if (localPasswordFail) {
            applyBusinessLoginFailure(offlineAuthService, offlineLoginResult);
          } else {
            setError(ta('errNoInternet'));
          }
          setIsLoading(false);
          return;
        }

        if (!result.success && localPasswordFail) {
          // Cloud also rejects — show credentials error (not “local only” wording alone)
          applyBusinessLoginFailure(offlineAuthService, offlineLoginResult);
          setIsLoading(false);
          return;
        }
        if (result.success) {
          // Prefer local offline bucket when the same account exists — avoids cloud UUID
          // vs local slug split (catalog under offline id, session under cloud id).
          let localBusiness = null;
          if (hasLocalAccount) {
            localBusiness = offlineAuthService.getBusinesses().find((b) => {
              const u = String(b.username || '').trim().toLowerCase();
              const e = String(b.email || '').trim().toLowerCase();
              return u === normalizedLogin || e === normalizedLogin;
            });
            if (localBusiness?.id) {
              offlineAuthService.updateBusiness(localBusiness.id, {
                password: await offlineAuthService.hashPassword(String(form.password || '').trim()),
                manager_code: result.business.manager_code || localBusiness.manager_code,
                supabaseAuth: true,
                cloudId: result.business.id,
                cloud_plan: result.business.cloud_plan || 'none',
                subscription_type: result.business.subscription_type || null,
                subscription_amount: result.business.subscription_amount ?? null,
                subscription_expiry_date: result.business.subscription_expiry_date || null
              });
              if (result.business.manager_code) {
                persistManagerCode(result.business.manager_code);
              }

              // If cloud row has null manager_code but local has hash, push now (owner session)
              try {
                const localHash = localBusiness.manager_code;
                if (localHash && !result.business.manager_code) {
                  const { supabase } = await import('../config/supabase');
                  await supabase
                    .from('businesses')
                    .update({
                      manager_code: localHash,
                      updated_at: new Date().toISOString()
                    })
                    .eq('id', result.business.id);
                  result.business = { ...result.business, manager_code: localHash };
                }
              } catch {
                /* optional */
              }

              try {
                const { serializeBusinessTypeForCloud } = await import('../utils/businessTypeCloud');
                const { getBusinessTypeId } = await import('../utils/restaurantMode');
                const { businessService } = await import('../services/supabaseService');
                const localType = serializeBusinessTypeForCloud(localBusiness.business_type);
                const cloudTypeId = getBusinessTypeId(result.business.business_type);
                if (localType?.id && localType.id !== cloudTypeId) {
                  await businessService.updateBusinessType(result.business.id, localType);
                }
              } catch (typeSyncErr) {
                console.warn('Business type cloud sync skipped:', typeSyncErr?.message || typeSyncErr);
              }
            }
          } else {
            // Netlify / neuer Browser: Cloud-Konto lokal spiegeln → Login ohne erneute Registrierung
            try {
              const mirrored = await offlineAuthService.upsertBusinessFromCloud(
                result.business,
                form.password
              );
              if (mirrored.success) {
                localBusiness = mirrored.business;
              }
            } catch (mirrorErr) {
              console.warn('Cloud → local business mirror skipped:', mirrorErr?.message || mirrorErr);
            }
          }

          const cloudBusinessId = result.business.id;
          // Manager/owner: any PC. Do NOT bind/rebind business machine_id here
          // (empty local KV + first-bind would steal the POS from cashiers).
          // POS machine_id is set at registration or on first cashier login.
          try {
            const { machineBindingService } = await import('../services/machineBindingService');
            await machineBindingService.ensureInstallationBinding({ allowRebind: true });
          } catch {
            /* non-blocking for manager */
          }
          // Load business info including logo from Supabase
          let businessLogo = null;
          try {
            const businessInfo = await businessInfoService.getBusinessInfo(cloudBusinessId);
            if (businessInfo && businessInfo.logo) {
              businessLogo = businessInfo.logo;
            }
          } catch (logoError) {
            console.warn('Error loading logo during login:', logoError);
            // Continue without logo if loading fails
          }

          const { normalizeBusinessStorageId } = await import('../utils/sharedCore');
          const cloudStorageId =
            normalizeBusinessStorageId(cloudBusinessId) || String(cloudBusinessId);
          const localStorageId = localBusiness?.id
            ? normalizeBusinessStorageId(localBusiness.id) || String(localBusiness.id)
            : null;
          // Session / entity bucket = local when present; keep cloudId for sync/binding
          const storageId = localStorageId || cloudStorageId;

          // Prepare bootstrap so authStateChanged always runs a fresh load
          try {
            const { prepareBootstrapForLogin } = await import(
              '../features/persistence/hooks/useBusinessBootstrap'
            );
            prepareBootstrapForLogin(storageId);
          } catch {
            /* optional */
          }

          // Store business user data via Storage Engine for compatibility
          const user = {
            type: 'business',
            user: {
              id: storageId,
              email: result.business.email,
              user_metadata: {
                business_name: result.business.business_name,
                owner_name: result.business.owner_name,
                business_type: result.business.business_type
              }
            },
            business: {
              ...result.business,
              // Storage slug — matches entity_* / business_*_data keys
              id: storageId,
              auth_user_id: storageId,
              cloudId: cloudBusinessId,
              // Include logo if available
              logo: businessLogo || result.business.logo || null
            },
            supabaseAuth: true
          };
          const currentBusiness = {
            ...result.business,
            id: storageId,
            businessId: storageId,
            cloudId: cloudBusinessId,
            business_name: result.business.business_name || result.business.name,
            name: result.business.business_name || result.business.name,
            logo: businessLogo || result.business.logo || null
          };
          persistBusinessSessionPair(user, currentBusiness, {
            managerCode: result.business.manager_code,
            dispatchAuthEvent: true
          });

          try {
            const { markInstallationConfigured } = await import(
              '../utils/installationRegistrationPolicy'
            );
            markInstallationConfigured();
          } catch {
            /* non-blocking */
          }

          clearBusinessLogoutIntent();

          try {
            const { pinCurrentBusiness } = await import(
              '../infrastructure/storage/businessIdDiscovery'
            );
            pinCurrentBusiness(storageId);
          } catch {
            /* optional */
          }

          // Never auto-grant manager — always require CodeEntry PIN after password
          const { persistRoleForBusiness } = await import('../utils/roleStorage');
          persistRoleForBusiness(storageId, null, { allowRemove: true });
          lockoutService.resetLockoutOnSuccess();
          try {
            const { drainPendingSyncOnce } = await import('../domain/sync/electronSyncOrchestrator');
            void drainPendingSyncOnce(40).catch(() => {});
          } catch {
            /* sync optional */
          }
          try {
            const { scheduleAutomaticCloudSync } = await import(
              '../services/parallelCloudFieldSync'
            );
            scheduleAutomaticCloudSync(storageId, { reason: 'login' });
          } catch {
            /* full cloud optional */
          }
          warmManagerRoutesAfterLogin();
          setIsLoading(false);
          return;
        }
      }

      // If we get here, login failed
      if (result && result.error) {
        const cloudErr = String(result.error || '');
        const pendingOrMissing =
          /approuv/i.test(cloudErr) ||
          /introuvable/i.test(cloudErr) ||
          /not yet approved/i.test(cloudErr) ||
          /not found/i.test(cloudErr);
        if (offlineAccountMissing && pendingOrMissing) {
          setError(ta('errNoLocalWaitCloud'));
        } else if (offlineAccountMissing) {
          // Prefer real cloud error (wrong password, network) over "no local account"
          setError(cloudErr || (registrationAllowed ? ta('errNoLocalUseRegister') : ta('errNoLocalLoginOnly')));
        } else if (hasLocalAccount || loginMode === 'business') {
          applyBusinessLoginFailure(offlineAuthService, offlineLoginResult);
        } else {
          setError(cloudErr);
        }
      } else if (offlineAccountMissing) {
        setError(registrationAllowed ? ta('errNoLocalCreate') : ta('errNoLocalLoginOnly'));
      } else if (hasLocalAccount || loginMode === 'business') {
        applyBusinessLoginFailure(offlineAuthService, offlineLoginResult);
      } else {
        setError(ta('errInvalidCredentials'));
      }
    } catch (err) {
      console.error('Login error:', err);
      // Never overwrite a clear credential error with a generic connection message
      setError((prev) => (prev ? prev : ta('errConnection')));
    } finally {
      setIsLoading(false);
    }
  };

  const year = new Date().getFullYear();
  const versionText = ta('versionLabel', { version: APP_VERSION });

  return (
    <div className="login-page ads-auth-root aman-auth" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="login-page__backdrop" aria-hidden>
        <span className="login-page__geo login-page__geo--a" />
        <span className="login-page__geo login-page__geo--b" />
        <span className="login-page__geo login-page__geo--c" />
      </div>

      <div className="login-container">
        <aside className="login-header" aria-label="AMAN ERP">
          <div className="login-header__brand">
            <div className="auth-brand-logo-ring">
              <AuthBrandLogo size={88} />
            </div>
            <h1 className="auth-brand-title">
              <span>AMAN</span>
              <span className="auth-brand-title__product">ERP</span>
            </h1>
            <p className="auth-brand-tagline">{ta('brandTagline')}</p>
            <p className="auth-brand-description">{ta('brandDescription')}</p>

            {!registrationAllowed && (
              <p className="auth-login-only-notice">{ta('loginOnlyInstallation')}</p>
            )}
          </div>

          <footer className="login-header__footer">
            <p className="login-header__meta">
              <span>{versionText}</span>
              <span className="login-header__meta-sep" aria-hidden>
                ·
              </span>
              <span>© {year} AMAN ERP</span>
            </p>
          </footer>
        </aside>

        <main className="login-form-container">
          <div className="login-form">
            <div className="login-form__chrome">
              <div
                className={`login-connection-pill ${isOnline ? 'is-online' : 'is-offline'}`}
                title={isOnline ? ta('statusOnline') : ta('statusOffline')}
              >
                <span className="login-connection-pill__dot" aria-hidden />
                {isOnline ? <Wifi size={13} aria-hidden /> : <WifiOff size={13} aria-hidden />}
                <span>{isOnline ? ta('statusOnline') : ta('statusOffline')}</span>
              </div>
              <div className="language-switcher-container">
                <LanguageSwitcher />
              </div>
            </div>

            <header className="login-form__top">
              <h2 className="login-form__heading">{ta('loginTitle')}</h2>
              <p className="login-form__description">{ta('loginDescription')}</p>
            </header>

            <div className="login-mode-toggle" role="group" aria-label={ta('loginTitle')}>
              <button
                type="button"
                aria-pressed={loginMode === 'business'}
                className={loginMode === 'business' ? 'active' : ''}
                onClick={() => switchLoginMode('business')}
              >
                <Building2 size={16} aria-hidden />
                {ta('loginModeBusiness')}
              </button>
              <button
                type="button"
                aria-pressed={loginMode === 'worker'}
                className={loginMode === 'worker' ? 'active' : ''}
                onClick={() => switchLoginMode('worker')}
              >
                <BadgeCheck size={16} aria-hidden />
                {ta('loginModeWorker')}
              </button>
            </div>

            {error && (
              <div className="error-message" role="alert">
                {error}
                {loginMode === 'business' && loginDebug && (
                  <div className="login-debug-panel">
                    <button
                      type="button"
                      className="login-debug-toggle"
                      onClick={() => setShowLoginDebug((v) => !v)}
                      aria-expanded={showLoginDebug}
                    >
                      {showLoginDebug ? ta('debugHideDetails') : ta('debugShowDetails')}
                    </button>
                    {showLoginDebug && (
                      <ul className="login-debug-details">
                        {formatLoginDebugLines(loginDebug, ta).map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    )}
                    {!showLoginDebug && (
                      <p className="login-debug-hint">{ta('authRepairHintForgot')}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {loginMode === 'worker' && (
              <p className="login-worker-hint">{ta('workerLoginHint')}</p>
            )}

            <form onSubmit={handleSubmit} className="login-form__fields">
              <div className={`aman-auth-field${form.username ? ' is-populated' : ''}`}>
                <User size={18} className="aman-auth-field__icon" aria-hidden />
                <input
                  id="login-username"
                  ref={usernameRef}
                  className="aman-auth-field__input"
                  type="text"
                  autoComplete="username"
                  value={form.username}
                  onChange={(e) => handleChange('username', e.target.value)}
                  placeholder=" "
                  required
                />
                <label className="aman-auth-field__label" htmlFor="login-username">
                  {loginMode === 'worker' ? ta('workerNameLabel') : ta('usernameLabel')}
                </label>
              </div>

              <div
                className={`aman-auth-field aman-auth-field--password${
                  form.password ? ' is-populated' : ''
                }`}
              >
                <Lock size={18} className="aman-auth-field__icon" aria-hidden />
                <input
                  id="login-password"
                  className="aman-auth-field__input"
                  type={loginMode === 'worker' || showPassword ? 'text' : 'password'}
                  autoComplete={loginMode === 'worker' ? 'off' : 'current-password'}
                  value={form.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder=" "
                  required
                />
                <label className="aman-auth-field__label" htmlFor="login-password">
                  {loginMode === 'worker' ? ta('workerCodeLabel') : ta('passwordLabel')}
                </label>
                {loginMode === 'business' && (
                  <button
                    type="button"
                    className="aman-auth-field__toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
                  </button>
                )}
              </div>

              <div className="login-form__row">
                <label className="aman-auth-check">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>{ta('rememberMe')}</span>
                </label>
                {loginMode === 'business' && (
                  <button
                    type="button"
                    className="forgot-password-link"
                    onClick={onShowForgotPassword}
                  >
                    {ta('forgotPasswordLink')}
                  </button>
                )}
              </div>

              <button type="submit" className="login-button" disabled={isLoading}>
                {isLoading ? (
                  <span className="loading-spinner" aria-hidden />
                ) : (
                  <>
                    {ta('signIn')}
                    <ArrowRight size={18} className="ads-icon-dir" aria-hidden />
                  </>
                )}
              </button>
            </form>

            {registrationAllowed ? (
              <div className="login-footer">
                <button type="button" className="register-link" onClick={onShowRegister}>
                  <UserPlus size={16} aria-hidden />
                  {ta('createAccount')}
                </button>
              </div>
            ) : null}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Login;
